import { useCallback, useMemo } from 'react';

import type { RegisterSection, SectionId } from '../model/operations';
import { useCustomizationDispatch } from '../state/useCustomizationDispatch';
import type { RowActionId, SectionActionId } from '../viewmodels/rowTypes';
import type { CustomizationCommands } from '../commands/CustomizationCommandsContext';
import { useOperationDeps } from './useOperationDeps';
import { useQuestionActions } from './useQuestionActions';
import { useReorderActions } from './useReorderActions';
import { useSectionActions } from './useSectionActions';

type NavigationHandlers = {
  registerSection: RegisterSection;
  handleSectionSelect: (sectionId: SectionId) => void;
  handleRefresh: () => void;
};

export function useCustomizationCommands({
  registerSection,
  handleSectionSelect,
  handleRefresh,
}: NavigationHandlers): CustomizationCommands {
  const dispatch = useCustomizationDispatch();
  const operationDeps = useOperationDeps();
  const {
    hideQuestion,
    unhideQuestion,
    retry: retryQuestion,
  } = useQuestionActions(operationDeps);
  const {
    hideSection,
    unhideSection,
    retry: retrySection,
  } = useSectionActions(operationDeps);
  const { reorderSections, reorderQuestions } =
    useReorderActions(operationDeps);

  const selectQuestionEntry = useCallback(
    (questionId: string, entryId: RowActionId) => {
      const actionByEntry: Record<RowActionId, (id: string) => Promise<void>> =
        { hide: hideQuestion, unhide: unhideQuestion };

      actionByEntry[entryId](questionId);
    },
    [hideQuestion, unhideQuestion]
  );

  const selectSectionEntry = useCallback(
    (sectionId: string, entryId: SectionActionId) => {
      const actionByEntry: Record<
        SectionActionId,
        (id: string) => Promise<void>
      > = { hide: hideSection, unhide: unhideSection };

      actionByEntry[entryId](sectionId);
    },
    [hideSection, unhideSection]
  );

  const toggleSection = useCallback(
    (sectionId: string) => {
      dispatch({ type: 'SECTION_TOGGLED', sectionId });
    },
    [dispatch]
  );

  return useMemo(
    () => ({
      section: {
        selectEntry: selectSectionEntry,
        retry: retrySection,
        toggle: toggleSection,
        register: registerSection,
        reorder: reorderSections,
      },
      question: {
        selectEntry: selectQuestionEntry,
        retry: retryQuestion,
        reorder: reorderQuestions,
      },
      navigation: {
        selectSection: handleSectionSelect,
        refresh: handleRefresh,
      },
    }),
    [
      selectSectionEntry,
      retrySection,
      toggleSection,
      registerSection,
      reorderSections,
      selectQuestionEntry,
      retryQuestion,
      reorderQuestions,
      handleSectionSelect,
      handleRefresh,
    ]
  );
}
